import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/SimpleToast";
import { mailingApi } from "@/lib/api";
import { ImagePlus, Loader2, Check, Palette, Sparkles } from "lucide-react";

export function AiImageDialog({ open, onOpenChange, onGenerated }) {
  const toast = useToast();
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("brand");
  const [result, setResult] = useState(null); // { path }
  const [loading, setLoading] = useState(false);

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const previewSrc = result?.path ? `${backendUrl}${result.path}` : null;

  const reset = () => { setPrompt(""); setResult(null); setMode("brand"); };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Bitte eine Bildbeschreibung eingeben"); return; }
    setLoading(true);
    try {
      const res = await mailingApi.aiImage({ prompt: prompt.trim(), mode });
      setResult({ path: res.data.path });
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Bildgenerierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const use = () => {
    if (!result?.path) return;
    onGenerated(result.path);
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="ai-image-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ImagePlus className="w-5 h-5 text-primary" /> KI-Bildgenerator</DialogTitle>
          <DialogDescription>Beschreibe das gewünschte Bild. Der Brand-Modus stimmt die Farben auf unser Logo ab.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="ai-img-prompt">Bildbeschreibung</Label>
            <Textarea id="ai-img-prompt" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="z. B. IT-Techniker konfiguriert eine Firewall im Serverraum" data-testid="ai-image-prompt" />
          </div>
          <div className="space-y-2">
            <Label>Stil</Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setMode("brand")}
                className={`flex items-start gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${mode === "brand" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
                data-testid="ai-image-mode-brand">
                <Palette className={`w-4 h-4 mt-0.5 ${mode === "brand" ? "text-primary" : "text-muted-foreground"}`} />
                <span><span className="font-medium">Brand</span><span className="block text-xs text-muted-foreground">Farben passend zum Logo</span></span>
              </button>
              <button type="button" onClick={() => setMode("creative")}
                className={`flex items-start gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${mode === "creative" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
                data-testid="ai-image-mode-creative">
                <Sparkles className={`w-4 h-4 mt-0.5 ${mode === "creative" ? "text-primary" : "text-muted-foreground"}`} />
                <span><span className="font-medium">Kreativ</span><span className="block text-xs text-muted-foreground">Thematisch frei</span></span>
              </button>
            </div>
          </div>
          <Button type="button" onClick={generate} disabled={loading} className="w-full" data-testid="ai-image-generate">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
            {loading ? "Bild wird erstellt… (bis ~30 s)" : result ? "Neu generieren" : "Bild erzeugen"}
          </Button>
          {previewSrc && (
            <img src={previewSrc} alt="KI-Vorschau" className="w-full rounded-lg border" data-testid="ai-image-preview" />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { onOpenChange(false); reset(); }}>Abbrechen</Button>
          <Button type="button" onClick={use} disabled={!result?.path} data-testid="ai-image-use">
            <Check className="w-4 h-4 mr-2" /> Bild übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
